import type { ETrustedAggregateRating } from "./client.server";

export type ETrustedAggregatePeriod = keyof ETrustedAggregateRating;

export interface CombinedRating {
  rating: number;
  reviewCount: number;
}

export function getRatingForPeriod(
  channelScore: ETrustedAggregateRating | null | undefined,
  period: ETrustedAggregatePeriod
): CombinedRating {
  const periodScore = channelScore?.[period];

  return {
    rating: periodScore?.rating ?? 0,
    reviewCount: periodScore?.count ?? 0,
  };
}

/**
 * Calculate a weighted aggregate rating across all channels for a specific period.
 *
 * Formula: (rating1 * count1 + rating2 * count2 + ...) / totalCount
 */
export function calculateCombinedRatingForPeriod(
  allChannelScores: Record<string, ETrustedAggregateRating>,
  period: ETrustedAggregatePeriod
): CombinedRating {
  let totalCount = 0;
  let weightedSum = 0;

  Object.values(allChannelScores).forEach((channelScore) => {
    const periodScore = channelScore?.[period];
    if (!periodScore) {
      return;
    }

    totalCount += periodScore.count;
    weightedSum += periodScore.rating * periodScore.count;
  });

  return {
    rating: totalCount > 0 ? weightedSum / totalCount : 0,
    reviewCount: totalCount,
  };
}

/**
 * Calculate combined weighted average rating from all channel scores
 * using the last 365 days period.
 */
export function calculateCombinedRating(
  allChannelScores: Record<string, ETrustedAggregateRating>
): CombinedRating {
  return calculateCombinedRatingForPeriod(allChannelScores, "365days");
}


